import { Box, Paper, Typography } from "@mui/material";

import { Cabecalho, Cartao, dataBr, ErroAba, Kpi, num, plural, reais } from "./comum";
import { Direcao, IRhAbas, Mao, SeloVisibilidadeAba } from "./types";
import { SeloVisibilidade } from "./Visibilidade";

const LINHAS: { mao: Mao; rotulo: string; quem: string }[] = [
  { mao: "RH", rotulo: "Aprovar", quem: "com você (RH)" },
  { mao: "CORRETORA_ENVIA", rotulo: "Enviar", quem: "com a corretora" },
  { mao: "CORRETORA_COBRA", rotulo: "Cobrar a operadora", quem: "com a corretora" },
  { mao: "CALENDARIO", rotulo: "Só o calendário", quem: "data ainda longe" },
];
const COLUNAS: { direcao: Direcao; rotulo: string; nota: string }[] = [
  { direcao: "INCLUSAO", rotulo: "Inclusões", nota: "atrasar custa carência" },
  { direcao: "EXCLUSAO", rotulo: "Exclusões", nota: "atrasar custa mensalidade" },
  { direcao: "TRANSFERENCIA", rotulo: "Transferências", nota: "" },
  { direcao: "ALTERACAO", rotulo: "Alterações", nota: "" },
];
const fundo = (atrasadas: number) =>
  atrasadas === 0 ? "#FFFFFF" : atrasadas < 10 ? "#FDECEC" : atrasadas < 60 ? "#F8C9C9" : "#F09A9A";

/** Aba "Compras e movimentações" (escopo 2.3 §3 e §4c, catálogo M8 a M16). */
export function ComprasTab({ dados, erro, selo }: { dados?: IRhAbas; erro: boolean; selo?: SeloVisibilidadeAba | null }) {
  if (erro || !dados?.compras) return <ErroAba nome="Compras e movimentações" />;
  const c = dados.compras;
  const custo = c.custoDeEsperar;

  return (
    <Box display="flex" flexDirection="column" gap={3} maxWidth={1200}>
      <Cabecalho
        sobretitulo="Compras e movimentações"
        titulo="O que entrou, saiu e está parado"
        dados={dados}
        unidade="por inscrição: quem está em dois contratos conta nos dois, salvo as coberturas, contadas por pessoa"
        aoLadoDoTitulo={<SeloVisibilidade selo={selo} />}
      />

      {custo.n > 0 && (
        <Paper sx={{ bgcolor: "#0F0F1E", color: "#FFFFFF", p: 3.5, display: "flex", flexDirection: "column", gap: 1, borderRadius: 3 }}>
          <Typography variant="body2" sx={{ color: "#31F7FF", fontWeight: 600 }}>
            O custo de esperar
          </Typography>
          <Typography
            data-testid="custo-valor"
            sx={{ fontFamily: "Sora", fontSize: { xs: "2rem", md: "3rem" }, fontWeight: 700, lineHeight: 1.1, color: "#FFFFFF" }}
          >
            {custo.rsMes === null ? plural(custo.n, "exclusão", "exclusões") : `${reais(custo.rsMes)} por mês`}
          </Typography>
          <Typography sx={{ color: "#D1D5DB" }}>
            {custo.rsMes === null
              ? `esperam a sua aprovação (${plural(custo.atrasadas, "atrasada", "atrasadas")}). O valor do plano não está apurado.`
              : custo.nComValor < custo.n
                ? `é a mensalidade de ${num(custo.nComValor)} das ${plural(custo.n, "exclusão", "exclusões")} que esperam a sua aprovação (${plural(custo.atrasadas, "atrasada", "atrasadas")}); as outras não têm valor de plano apurado.`
                : `é a mensalidade das ${plural(custo.n, "exclusão", "exclusões")} que esperam a sua aprovação (${plural(custo.atrasadas, "atrasada", "atrasadas")}).`}
          </Typography>
        </Paper>
      )}

      {c.mes && (
        <Box display="flex" flexDirection="column" gap={1}>
          <Typography component="h3" variant="h6" sx={{ fontFamily: "Sora", fontWeight: 700 }}>
            Este mês até agora
          </Typography>
          <Box display="grid" gap={2} gridTemplateColumns="repeat(auto-fit, minmax(200px, 1fr))">
            <Kpi
              rotulo="Coberturas confirmadas"
              valor={c.mes.cobertasNoMes === null ? null : num(c.mes.cobertasNoMes)}
              nota="pessoas com carteirinha encontrada na operadora"
            />
            <Kpi rotulo="Enviadas à operadora" valor={num(c.mes.enviadasNoMes)} nota="pela integração" />
            <Kpi
              rotulo="Saídas confirmadas"
              valor={c.mes.saidasConfirmadasNoMes === null ? null : num(c.mes.saidasConfirmadasNoMes)}
              nota="registradas pela operadora"
            />
            <Kpi
              rotulo="Saíram e seguem na fatura (hoje)"
              valor={c.mes.geramCusto ? num(c.mes.geramCusto.n) : null}
              nota={[
                c.mes.geramCusto?.valorMes != null ? `${reais(c.mes.geramCusto.valorMes)} por mês` : "valor não apurado",
                c.mes.geramCusto?.contratosSemFacto
                  ? `${plural(c.mes.geramCusto.contratosSemFacto, "contrato", "contratos")} sem custo apurado`
                  : null,
              ]
                .filter(Boolean)
                .join(" · ")}
            />
          </Box>
        </Box>
      )}

      <Cartao titulo="Onde está parado, e na mão de quem" subtitulo="Pessoas na fila hoje, por etapa e tipo de movimento. Quanto mais escuro, mais atrasadas.">
        <Box sx={{ overflowX: "auto" }}>
          <Box display="grid" gridTemplateColumns="170px repeat(4, minmax(140px, 1fr))" gap={0.75} minWidth={760}>
            <span />
            {COLUNAS.map((col) => (
              <Typography key={col.direcao} variant="caption" fontWeight={700} color="text.secondary">
                {col.rotulo}
                {col.nota && (
                  <>
                    <br />
                    <span style={{ fontWeight: 400 }}>{col.nota}</span>
                  </>
                )}
              </Typography>
            ))}
            {LINHAS.map((l) => [
              <Box key={l.mao} display="flex" flexDirection="column" justifyContent="center">
                <Typography variant="body2" fontWeight={700}>
                  {l.rotulo}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {l.quem}
                </Typography>
              </Box>,
              ...COLUNAS.map((col) => {
                const cel = c.fila.find((f) => f.mao === l.mao && f.direcao === col.direcao);
                const cal = l.mao === "CALENDARIO";
                // why: como no Balanço, a linha do calendário junta a data longe de todas as etapas (ENESA 18/10: 151 do RH sumiam).
                const total = cal
                  ? c.fila.filter((f) => f.direcao === col.direcao).reduce((s, f) => s + f.calendario, 0)
                  : cel ? cel.noPrazo + cel.vencendo + cel.atrasadas : 0;
                const atr = cel && !cal ? cel.atrasadas : 0;
                return (
                  <Box
                    key={`${l.mao}-${col.direcao}`}
                    data-testid={`fila-${l.mao}-${col.direcao}`}
                    sx={{ border: "1px solid #EEEFF2", borderRadius: 2, p: 1.5, minHeight: 80, bgcolor: cal ? "#FAFAFB" : fundo(atr) }}
                  >
                    <Typography sx={{ fontFamily: "Sora", fontSize: "1.5rem", fontWeight: 700, color: total ? "text.primary" : "text.disabled" }}>
                      {num(total)}
                    </Typography>
                    <Typography variant="caption" fontWeight={600} sx={{ color: atr ? "#9F1239" : "text.secondary" }}>
                      {cal ? (total ? "data ainda longe" : "") : atr ? plural(atr, "atrasada", "atrasadas") : total ? "nenhuma atrasada" : "nada na fila"}
                    </Typography>
                  </Box>
                );
              }),
            ])}
          </Box>
        </Box>
      </Cartao>

      <Box display="grid" gap={2} gridTemplateColumns="repeat(auto-fit, minmax(260px, 1fr))">
        {c.tempoParado.map((t) => (
          <Kpi
            key={t.rotulo}
            rotulo={t.rotulo}
            valor={t.medianaDias === null ? "ninguém" : plural(Math.round(t.medianaDias), "dia", "dias")}
            nota={
              t.n === 0
                ? "nenhuma pessoa nesta etapa"
                : `mediana de ${plural(t.n, "pessoa", "pessoas")} · ${num(t.acimaDe30)} há mais de 30 dias · a mais antiga há ${plural(t.maisAntigaDias ?? 0, "dia", "dias")}`
            }
          />
        ))}
      </Box>

      <Box display="grid" gap={2} gridTemplateColumns="repeat(auto-fit, minmax(340px, 1fr))">
        <Cartao titulo="Saídas agendadas" subtitulo="Já confirmadas pela operadora, valem na data abaixo.">
          {c.saidasAgendadas.length === 0 ? (
            <Typography color="text.secondary">Nenhuma saída agendada.</Typography>
          ) : (
            c.saidasAgendadas.map((s) => (
              <Box key={s.data} display="flex">
                <Typography>{dataBr(s.data)}</Typography>
                <Typography fontWeight={700} ml="auto">
                  {num(s.n)} {s.n === 1 ? "pessoa" : "pessoas"}
                </Typography>
              </Box>
            ))
          )}
        </Cartao>

        {c.eSe && (
          <Cartao
            titulo="E se a aprovação tivesse prazo?"
            subtitulo="Mensalidade paga pelos dias além do prazo, nas exclusões de quem já saiu da folha e espera a sua aprovação. Estimado pelo custo médio da fila."
          >
            {c.eSe.map((e) => (
              <Box key={e.prazoDias} display="flex">
                <Typography>Prazo de {e.prazoDias} dias</Typography>
                <Typography fontWeight={700} ml="auto">
                  {reais(e.rsEvitavel)}
                </Typography>
              </Box>
            ))}
          </Cartao>
        )}
      </Box>

      <Typography variant="body2" color="text.secondary">
        Histórico mês a mês de inclusões e exclusões: relatório Ciclo de Movimentação, no Insights.
      </Typography>
    </Box>
  );
}
